import * as React from 'react'
import { IRiderLocationObject, IRiderObject, IStateMetadata, IAutoComplete } from '../../../../types'
import { IGoogleMapBoundsDTO } from '../../../../types/google-map'
import { googleMapService } from '../../../../services/GoogleMap'
import { searchLocation, getPlaceDetails } from '../../../../utils/location'
import UIMessages from '../../../../utils/ui-messages'
import LocationItem from '../../common/LocationItem'
import RiderBottomBar from '../CreateRiderBottomBar'
import PinLocationModal from '../../../PinLocationModal'
import { Button } from '@mobiscroll/react-latest'
import FloatingForm from 'src/components/ui/form/FloatingForm'

interface ILocationProps {
  zoom: number
  bounds: IGoogleMapBoundsDTO
  metadata: IStateMetadata
  rider: IRiderObject
  riderLocations: IRiderLocationObject[]
  getRiderLocationList: (riderId: string) => void
  createLocation: (riderId: string, location: IRiderLocationObject) => Promise<IRiderLocationObject>
  saveLocations: (rider: IRiderObject, locations: IRiderLocationObject[]) => Promise<any>
  deleteLocation: (riderId: string, id: string) => Promise<any>
  showMessage: (error: string, isError: boolean) => void
  autoComplete: IAutoComplete[]
  showAutoComplete: (e: any) => Promise<any>
  clearAutoComplete: () => Promise<any>
  currentStep: number
  onCompleteStep: (step: number) => void
}

interface IRiderState {
  isSaved: boolean
  isSaving: boolean
  isOpen: boolean
  index: number
  locations: IRiderLocationObject[]
  locationIdsToUpdate: string[]
  newLocation: IRiderLocationObject | null
  locationToPin: IRiderLocationObject | null
  autoCompleteLocations: IRiderLocationObject[]
}

class LocationPane extends React.Component<ILocationProps, IRiderState> {
  constructor(props: ILocationProps) {
    super(props)
    this.state = {
      isSaved: true,
      isSaving: false,
      locations: [],
      locationIdsToUpdate: [],
      newLocation: null,
      locationToPin: null,
      isOpen: false,
      index: -1,
      autoCompleteLocations: []
    }
    this.props.onCompleteStep(this.props.currentStep)
  }

  public componentDidUpdate(prevProps: ILocationProps) {
    if (prevProps.metadata !== this.props.metadata) {
      const { processingError, processingSuccess } = this.props.metadata
      if (processingError) {
        this.setState({ isSaving: false, isSaved: false })
      }
      if (processingSuccess) {
        this.setState({ isSaving: false, isSaved: true })
      }
    }
  }

  public render() {
    const { isSaving, locations, newLocation, index, locationIdsToUpdate } = this.state
    const listOfLocationsStyle: any = {
      pointerEvents: isSaving ? 'none' : 'auto',
      opacity: isSaving ? 0.4 : 1.0
    }
    const addNewLocationButtonStyle: any = {
      pointerEvents: !!newLocation ? 'none' : 'auto',
      opacity: !!newLocation ? 0.4 : 1.0
    }

    return (
      <section>
        {this.state.isOpen && (
          <PinLocationModal
            zoom={this.props.zoom}
            bounds={this.props.bounds}
            location={this.state.locationToPin!}
            locations={this.state.locations}
            onCancel={this.onCancel}
            saveLocation={this.onModalLocationSaved}
          />
        )}
        <FloatingForm>
          <div style={listOfLocationsStyle} className='bg-white mb4 w100'>
            {locations.map((it, i) => (
              <LocationItem
                index={i}
                key={`tmli-${i}`}
                allowToEdit={!newLocation}
                location={it}
                searchLocation={this.searchLocation}
                onLocationSelect={this.onLocationSelect}
                autoCompleteLocations={index === i ? this.state.autoCompleteLocations : []}
                onCancel={this.onCancel}
                saveLocation={this.onSaveLocation}
                deleteLocation={this.deleteLocation}
                showMessage={this.props.showMessage}
                updateLocation={this.onUpdateLocation}
                openModal={this.openModal}
              />
            ))}
            {!!newLocation && (
              <LocationItem
                index={-1}
                allowToEdit={true}
                location={newLocation}
                searchLocation={this.searchLocation}
                onLocationSelect={this.onLocationSelect}
                autoCompleteLocations={index === -1 ? this.state.autoCompleteLocations : []}
                onCancel={this.onCancel}
                saveLocation={this.onSaveLocation}
                showMessage={this.props.showMessage}
                updateLocation={this.onUpdateLocation}
                openModal={this.openModal}
              />
            )}
            <div className='pl3' style={addNewLocationButtonStyle}>
              <Button className='mbsc-success-btn' onClick={this.onCreateLocation}>
                <div className='bold font-white fs-20 pr1'>+</div>
                {UIMessages.common.addLocation}
              </Button>
            </div>
          </div>
        </FloatingForm>
        <RiderBottomBar
          isSaved={this.state.isSaved}
          isSaving={this.state.isSaving}
          nextStepPath={'/riders/create/membership'}
          previousStepPath={'/riders/create/profile'}
          skipStepPath={'/riders/create/membership'}
          onSave={this.onSave}
          saveDisabled={!locationIdsToUpdate.length}
        />
      </section>
    )
  }

  private onModalLocationSaved = (location: IRiderLocationObject) => {
    this.onUpdateLocation(location, this.state.index)
  }

  private searchLocation = async (address: string, index: number) => {
    if (!address) {
      this.setState({
        autoCompleteLocations: []
      })
      return
    }
    const autoCompleteLocations = await searchLocation({ input: address })
    this.setState({
      autoCompleteLocations,
      index
    })
  }

  private onLocationSelect = async (selectedLocation: any, index: number) => {
    const { locations, locationIdsToUpdate } = this.state
    const { googlePlaceId } = selectedLocation

    const data = await getPlaceDetails(googlePlaceId)
    const location = { ...selectedLocation, ...data }
    //if user typed a name before address earch, keep it
    location.placeName = selectedLocation.placeName || location.placeName

    if (selectedLocation.isNew) {
      this.setState({
        newLocation: location,
        autoCompleteLocations: []
      })
    } else {
      const foundIndex = locationIdsToUpdate.indexOf(location.id)
      locations[index] = location
      this.setState({
        locations,
        autoCompleteLocations: [],
        locationIdsToUpdate: foundIndex < 0 ? [...locationIdsToUpdate, location.id] : locationIdsToUpdate
      })
    }
  }

  private onUpdateLocation = (location: IRiderLocationObject, index: number) => {
    const { locations, locationIdsToUpdate } = this.state

    if (location.isNew) {
      this.setState({
        isSaved: false,
        newLocation: location,
        isOpen: false
      })
    } else {
      const id = location.id!
      const foundIndex = locationIdsToUpdate.indexOf(id)
      locations[index] = location
      this.setState({
        locations,
        locationIdsToUpdate: foundIndex < 0 ? [...locationIdsToUpdate, id] : locationIdsToUpdate,
        isSaved: false,
        isOpen: false
      })
    }
  }

  private onSaveLocation = (location: IRiderLocationObject) => {
    if (!this.props.rider.id) {
      return
    }
    const { locations } = this.state

    this.props.createLocation(this.props.rider.id, location).then(
      (createdLoc: IRiderLocationObject) => {
        this.setState({
          isSaved: false,
          locations: [...this.state.locations, createdLoc],
          newLocation: null
        })
      },
      () => {
        this.setState({
          isSaved: false
        })
      }
    )
  }

  private deleteLocation = (index: number) => {
    if (!this.props.rider.id) {
      return
    }
    const { locations, locationIdsToUpdate } = this.state
    const location = locations[index]
    const id = location.id!
    const newLocations = [...locations.slice(0, index), ...locations.slice(index + 1)]
    const foundIndex = locationIdsToUpdate.indexOf(id)

    this.props.deleteLocation(this.props.rider.id, id).then(
      () =>
        this.setState({
          locations: newLocations,
          locationIdsToUpdate: [
            ...locationIdsToUpdate.slice(0, foundIndex),
            ...locationIdsToUpdate.slice(foundIndex + 1)
          ]
        }),
      () =>
        this.setState({
          locations
        })
    )
  }

  private onCancel = () => {
    this.setState({
      isSaved: false,
      isOpen: false,
      newLocation: null
    })
  }
  private onCreateLocation = () => {
    this.setState({
      isSaved: false,
      newLocation: { isNew: true } as IRiderLocationObject
    })
  }

  private onSave = () => {
    if (!this.props.rider.id) {
      return
    }
    const { locations, locationIdsToUpdate } = this.state
    this.setState({ isSaving: true })

    const locationsToUpdate = locations.filter((l: IRiderLocationObject) => locationIdsToUpdate.indexOf(l.id!) > -1)
    for (const location of locationsToUpdate) {
      if (location.fullAddress.length < 3) {
        this.showErrorMessage(UIMessages.rider.validation.locationAddressMinLength)
        return
      }
    }

    this.props.saveLocations(this.props.rider, locationsToUpdate).then(
      () => {
        this.setState({ isSaved: true, locationIdsToUpdate: [] })
      },
      () => this.setState({ isSaved: false, isSaving: false })
    )
  }

  private closeModal = () => {
    this.setState({
      isOpen: false
    })
  }

  private openModal = (location: IRiderLocationObject, index: number) => {
    if (!location.coordinates || (location.coordinates.lat === 0 && location.coordinates.lng === 0)) {
      return
    }

    this.setState({
      isOpen: true,
      index,
      locationToPin: location
    })
    this.props.showMessage('', false)
  }

  private showErrorMessage = (message: string) => {
    this.props.showMessage(message, true)
    this.setState({ isSaving: false })
  }
}

export default LocationPane



// WEBPACK FOOTER //
// ./src/components/riders/createrider/location/LocationPane.tsx